/**
 * ============================================
 * SAHINKAYA_GUNCELLEMESI - Customer Protocols JS
 * Tarih: 2026-05-16 (externalized from customer-protocols-section.liquid)
 * --------------------------------------------
 * Müşteri "Protokollerim" sayfasındaki accordion + JSON protokol parser.
 * Önceden section içinde inline <script> bloğundaydı; CDN cache ve CSP
 * uyumluluğu için ayrı dosyaya alındı. İçerikte hiç Liquid interpolasyonu
 * yok, bu yüzden 1:1 taşıma yapıldı + event delegation eklendi.
 *
 * Idempotent guard'lar (__mbluProtocol*Bound) Shopify section
 * yeniden render edildiğinde listener'ların ikiye katlanmasını önler.
 * ============================================
 */

function toggleProtocol(protocolId) {
  // Tüm accordion item'ları kapat + aria-expanded güncelle
  const allItems = document.querySelectorAll('.protocol-accordion-item');
  allItems.forEach(item => {
    if (!item.querySelector(`#protocol-${protocolId}`)) {
      item.classList.remove('active');
      const header = item.querySelector('.protocol-accordion-header');
      if (header) header.setAttribute('aria-expanded', 'false');
    }
  });

  // Tıklanan item'ı aç/kapat
  const targetItem = document.querySelector(`#protocol-${protocolId}`).closest('.protocol-accordion-item');
  targetItem.classList.toggle('active');
  const targetHeader = targetItem.querySelector('.protocol-accordion-header');
  if (targetHeader) {
    targetHeader.setAttribute('aria-expanded', targetItem.classList.contains('active') ? 'true' : 'false');
  }
}

// SAHINKAYA — A11y: Escape key collapses the active accordion item
// and returns focus to its header (WAI-ARIA Accordion pattern).
// Idempotent guard prevents double-binding on section re-render.
if (!window.__mbluProtocolEscBound) {
  window.__mbluProtocolEscBound = true;
  document.addEventListener('keydown', function(e) {
    if (e.key !== 'Escape') return;
    const focused = document.activeElement;
    if (!focused) return;
    const activeItem = focused.closest('.protocol-accordion-item.active');
    if (!activeItem) return;
    activeItem.classList.remove('active');
    const header = activeItem.querySelector('.protocol-accordion-header');
    if (header) {
      header.setAttribute('aria-expanded', 'false');
      header.focus();
    }
  });
}

// SAHINKAYA — CSP-safe event delegation: click + Enter/Space on any
// element with [data-protocol-id] triggers toggleProtocol.
// Replaces inline onclick/onkeydown that the section markup used to have.
if (!window.__mbluProtocolDelegateBound) {
  window.__mbluProtocolDelegateBound = true;
  document.addEventListener('click', function(e) {
    const trigger = e.target.closest('[data-protocol-id]');
    if (!trigger) return;
    toggleProtocol(trigger.dataset.protocolId);
  });
  document.addEventListener('keydown', function(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const trigger = e.target.closest('[data-protocol-id]');
    if (!trigger) return;
    e.preventDefault();
    toggleProtocol(trigger.dataset.protocolId);
  });
}

// Gelişmiş JSON protokol parser fonksiyonu
function parseProtocolContent(jsonString) {
  try {
    const data = JSON.parse(jsonString);
    const format = detectProtocolFormat(data);
    return renderProtocolContent(data, format);
  } catch (e) {
    return jsonString; // JSON değilse orijinal metni döndür
  }
}

// Format tespiti fonksiyonu
function detectProtocolFormat(node) {
  if (!node || typeof node !== 'object') return 'basic';

  // Recursive olarak tüm node'ları kontrol et
  function hasAdvancedFeatures(obj) {
    if (Array.isArray(obj)) {
      return obj.some(item => hasAdvancedFeatures(item));
    }

    if (typeof obj === 'object' && obj !== null) {
      // Heading varsa advanced format
      if (obj.type === 'heading') return true;

      // Bold text varsa advanced format
      if (obj.bold === true) return true;

      // Children'ları kontrol et
      if (obj.children) {
        return hasAdvancedFeatures(obj.children);
      }

      // Diğer properties'leri kontrol et
      return Object.values(obj).some(value => hasAdvancedFeatures(value));
    }

    return false;
  }

  return hasAdvancedFeatures(node) ? 'advanced' : 'basic';
}

// SAHINKAYA — HTML escape helper for protocol metafield content (XSS defense)
function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Ana render fonksiyonu
function renderProtocolContent(node, format = 'basic') {
  if (!node || typeof node !== 'object') return '';

  if (node.type === 'root' && node.children) {
    return node.children.map(child => renderProtocolContent(child, format)).join('');
  }

  // Heading desteği (Advanced format)
  if (node.type === 'heading' && node.children) {
    const content = node.children.map(child => renderProtocolContent(child, format)).join('');
    // SAHINKAYA — constrain to integer 1-6 to prevent tag injection via node.level
    const rawLevel = Number(node.level);
    const level = (rawLevel >= 1 && rawLevel <= 6) ? Math.floor(rawLevel) : 3;

    // Emoji'ye göre özel class ekle
    let className = 'protocol-heading';
    if (content.includes('☀️')) className += ' morning';
    else if (content.includes('🌙') || content.includes('🌝')) className += ' evening';
    else if (content.includes('🌊') || content.includes('📅')) className += ' weekly';

    return `<h${level} class="${className}">${content}</h${level}>`;
  }

  if (node.type === 'paragraph' && node.children) {
    const content = node.children.map(child => renderProtocolContent(child, format)).join('');
    if (!content.trim()) return '<br>';

    // Advanced format için gelişmiş kontroller
    if (format === 'advanced') {
      return renderAdvancedParagraph(content);
    }

    // Basic format için mevcut kontroller
    return renderBasicParagraph(content);
  }

  if (node.type === 'text' && node.value) {
    // SAHINKAYA — escape raw metafield text before any HTML wrapping (XSS defense).
    // highlightProductNames only adds <span> wrappers around matched patterns; the
    // captured groups it sees are already escaped, so the result is safe.
    const safe = escapeHtml(node.value);
    if (node.bold === true) {
      return `<strong>${highlightProductNames(safe)}</strong>`;
    }
    return highlightProductNames(safe);
  }

  return '';
}

// Basic format paragraph renderer
function renderBasicParagraph(content) {
  // Özel formatlar için kontrol
  if (content.includes('☀️SABAH:')) {
    return `<div class="routine-section morning"><h3><span class="routine-icon">☀️</span>SABAH RUTİNİ</h3></div>`;
  }
  if (content.includes('🌝AKŞAM:')) {
    return `<div class="routine-section evening"><h3><span class="routine-icon">🌝</span>AKŞAM RUTİNİ</h3></div>`;
  }
  if (content.includes('HAFTALIK BAKIM')) {
    return `<div class="routine-section weekly"><h3><span class="routine-icon">📅</span>HAFTALIK BAKIM</h3></div>`;
  }

  // Adım formatları
  if (content.match(/^🫧|^💧|^🌟|^☀️|^🌝|^💧💧💧|^📌/)) {
    return `<div class="protocol-step">${content}</div>`;
  }

  return `<p>${content}</p>`;
}

// Advanced format paragraph renderer
function renderAdvancedParagraph(content) {
  // Numaralandırılmış adımları tespit et
  if (content.match(/^[🫧💧🌟☀️🌝💧📌]\s*\d+\./)) {
    const stepNumber = content.match(/\d+/)[0];
    return `<div class="protocol-step numbered" data-step="${stepNumber}">${content}</div>`;
  }

  // Normal adım formatları
  if (content.match(/^🫧|^💧|^🌟|^☀️|^🌝|^💧💧💧|^📌/)) {
    return `<div class="protocol-step">${content}</div>`;
  }

  return `<p>${content}</p>`;
}

// Ürün isimlerini vurgula
function highlightProductNames(text) {
  return text.replace(
    /(Medicblu [^,.\n!?]+)/g,
    '<span class="product-name">$1</span>'
  ).replace(
    /(Serum [A-Za-z]+)/g,
    '<span class="product-name">$1</span>'
  ).replace(
    /(DANISAN30)/g,
    '<span class="discount-code">$1</span>'
  );
}

// Sayfa yüklendiğinde protokol içeriklerini parse et
document.addEventListener('DOMContentLoaded', function() {
  const firstItem = document.querySelector('.protocol-accordion-item');
  if (firstItem) {
    firstItem.classList.add('active');
  }

  // İlk protokol title'ını da aç (eğer collapsible ise)
  const firstTitleContent = document.querySelector('.protocol-title-content');
  const firstTitleCollapsible = document.querySelector('.protocol-title-collapsible');
  if (firstTitleContent && firstTitleCollapsible) {
    firstTitleContent.classList.add('active');
    firstTitleCollapsible.classList.add('active');
  }

  // Protokol içeriklerini parse et
  const protocolTexts = document.querySelectorAll('.protocol-text[data-protocol-content]');
  protocolTexts.forEach(element => {
    const jsonContent = element.getAttribute('data-protocol-content');
    if (jsonContent) {
      try {
        const data = JSON.parse(jsonContent);
        const format = detectProtocolFormat(data);
        const parsedContent = renderProtocolContent(data, format);

        element.setAttribute('data-format', format);
        element.innerHTML = parsedContent;
      } catch (e) {
        console.error('Protocol parsing error:', e);
        // SAHINKAYA — use textContent on fallback to prevent raw metafield HTML rendering
        element.textContent = jsonContent;
      }
    }
  });
});
